import React from 'react';
import { ArrowRight, Download, Github, Linkedin, Mail, MessageSquareMore  } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePortfolio } from '@/context/PortfolioContext';

const Hero = () => {
  const { portfolioData, loading, error } = usePortfolio();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const shellClass =
    'relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-white pt-16 dark:bg-[#0a0e1a] transition-colors duration-300';

  if (loading) {
    return (
      <section id="hero" className={shellClass}>
        <div className="text-center px-4">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-emerald-500 sm:h-12 sm:w-12" />
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 sm:text-base">Chargement...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="hero" className={shellClass}>
        <p className="px-4 text-center text-red-500">Erreur: {error}</p>
      </section>
    );
  }

  const hero = portfolioData?.hero;

  return (
    <section id="hero" className={shellClass}>
      <div className="pointer-events-none absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.12) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(16, 185, 129, 0.12) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="pointer-events-none absolute top-1/4 left-1/4 h-48 w-48 rounded-full bg-emerald-500/20 blur-3xl animate-blob sm:h-72 sm:w-72 lg:h-96 lg:w-96" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 h-48 w-48 rounded-full bg-teal-500/20 blur-3xl animate-blob animation-delay-2000 sm:h-72 sm:w-72 lg:h-96 lg:w-96" />

      <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center lg:max-w-5xl">
          <h1 className="animate-fade-in-up mb-4 text-3xl font-bold leading-tight text-gray-900 dark:text-white sm:mb-6 sm:text-5xl md:text-6xl lg:text-7xl">
            {hero?.name || 'Nom non disponible'}
          </h1>

          <div className="mb-4 sm:mb-6">
            <h2 className="animate-gradient-x bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-lg font-medium text-transparent sm:text-2xl lg:text-3xl">
              {hero?.title || 'Titre non disponible'}
            </h2>
          </div>

          <p className="animation-delay-200 animate-fade-in-up mx-auto mb-8 max-w-2xl px-2 text-base leading-relaxed text-gray-600 dark:text-gray-400 sm:mb-10 sm:text-lg md:text-xl">
            {hero?.tagline || 'Description non disponible'}
          </p>

          <div className="mb-8 flex w-full flex-col items-stretch justify-center gap-3 px-2 sm:mb-10 sm:flex-row sm:items-center sm:gap-4 sm:px-0">
            <Button
              onClick={() => scrollToSection('projects')}
              size="lg"
              className="w-full bg-emerald-500 px-6 py-5 text-base text-white hover:bg-emerald-600 sm:w-auto sm:px-8 sm:py-6 sm:text-lg motion-safe:transition-all motion-safe:hover:shadow-lg motion-safe:hover:shadow-emerald-500/50"
            >
              Voir mes projets
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              variant="outline"
              className="w-full border-2 border-emerald-500 px-6 py-5 text-base text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400 sm:w-auto sm:px-8 sm:py-6 sm:text-lg"
            >
              Me contacter
            </Button>
          </div>

          <div className="flex items-center justify-center gap-5 sm:gap-6">
            <a
              href={hero?.whatsapp || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full p-2 text-gray-600 transition-colors hover:text-emerald-400 dark:text-gray-400"
              aria-label="WhatsApp"
            >
              <MessageSquareMore className="h-6 w-6 sm:h-7 sm:w-7" />
            </a>
            <a
              href={hero?.github || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full p-2 text-gray-600 transition-colors hover:text-emerald-400 dark:text-gray-400"
              aria-label="GitHub"
            >
              <Github className="h-6 w-6 sm:h-7 sm:w-7" />
            </a>
            <a
              href={hero?.linkedin || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full p-2 text-gray-600 transition-colors hover:text-emerald-400 dark:text-gray-400"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-6 w-6 sm:h-7 sm:w-7" />
            </a>
            <a
              href={`mailto:${hero?.email || ''}`}
              className="rounded-full p-2 text-gray-600 transition-colors hover:text-emerald-400 dark:text-gray-400"
              aria-label="Email"
            >
              <Mail className="h-6 w-6 sm:h-7 sm:w-7" />
            </a>
          </div>

          {hero?.cv ? (
            <div className="mt-6 sm:mt-8">
              <a
                href={hero.cv}
                download
                className="inline-flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-emerald-400 dark:text-gray-400 sm:text-base"
              >
                <Download className="h-5 w-5" />
                <span>Télécharger mon CV</span>
              </a>
            </div>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={() => scrollToSection('about')}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 animate-bounce sm:block"
        aria-label="Aller à la section suivante"
      >
        <div className="flex h-10 w-6 items-start justify-center rounded-full border-2 border-emerald-400 p-2">
          <div className="h-3 w-1 rounded-full bg-emerald-400" />
        </div>
      </button>
    </section>
  );
};

export default Hero;
